import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { AuthGuard } from './guards/auth/auth.guard';
import { IsGuestGuard } from './guards/is-guest/is-guest.guard';
import { HasPermissionsGuard } from './guards/has-permissions/has-permissions.guard';
import { IsClientGuard } from './guards/is-client/is-client.guard';
import { noninclusiveProjectModulePermissions } from './project/project.permissions';
import { PermissionEnum } from './enums/permissions.enum';
import { PostulatedFormComponent } from './shared/postulated-form/postulated-form.component';
import { SignContractComponent } from './shared/sign-contract/sign-contract.component';

const routes: Routes = [
  {
    path: '',
    loadChildren: () => import('./home/home.module').then((m) => m.HomeModule),
    canActivate: [AuthGuard],
  },
  {
    path: 'project',
    loadChildren: () => import('./project/project.module').then((m) => m.ProjectModule),
    data: { permission: noninclusiveProjectModulePermissions, inclusivePermission: false },
    canActivate: [HasPermissionsGuard],
    canLoad: [HasPermissionsGuard],
  },
  {
    path: 'super-admin',
    loadChildren: () => import('./super-admin/super-admin.module').then((m) => m.SuperAdminModule),
    data: {
      permission: [PermissionEnum.READ_USERS, PermissionEnum.READ_ROLES, PermissionEnum.READ_PERMISSIONS, PermissionEnum.SUPERADMIN_READ_PROJECTS],
      inclusivePermission: false,
    },
    canActivate: [HasPermissionsGuard],
    canLoad: [HasPermissionsGuard],
  },
  {
    path: 'general-admin',
    loadChildren: () => import('./general-admin/general-admin.module').then((m) => m.GeneralAdminModule),
    data: {
      permission: [PermissionEnum.READ_PROJECTS_HOSTING, PermissionEnum.CREATE_PROJECTS_HOSTING, PermissionEnum.DELETE_PROJECTS_HOSTING],
      inclusivePermission: false,
    },
    canActivate: [HasPermissionsGuard],
    canLoad: [HasPermissionsGuard],
  },
  {
    // Unguarded on purpose: the shareable setup link (`/connect-domain/:token`) is the credential for someone who owns
    // the DNS but has no PineappleCMS login; the backend validates the token (one project, expiry, revocable).
    path: 'connect-domain',
    loadChildren: () => import('./domain-connect/connect-domain-public.module').then((m) => m.ConnectDomainPublicModule),
  },
  {
    // Unguarded on purpose: `callback` is the ONE URL registered with the DNS provider and is opened by a browser with no
    // session on this origin; it only hands the browser back to the app origin the user started from. The guarded
    // `complete` child route (see DomainConnectModule) does the authenticated work.
    path: 'domain-connect',
    loadChildren: () => import('./domain-connect/domain-connect.module').then((m) => m.DomainConnectModule),
  },
  {
    path: 'postulated-form',
    children: [
      {
        path: ':jobOfferId/:postulationId/:type',
        component: PostulatedFormComponent,
      },
    ],
  },
  {
    path: 'sign-contract',
    children: [
      {
        path: ':projectId/:id/:signerToken',
        component: SignContractComponent,
      },
      {
        path: ':id/:signerToken',
        component: SignContractComponent,
      },
      {
        path: ':id',
        component: SignContractComponent,
      },
    ],
  },
  {
    path: 'auth',
    loadChildren: () => import('./auth/auth.module').then((m) => m.AuthModule),
    canLoad: [IsGuestGuard],
  },
  {
    path: 'client-portal',
    loadChildren: () => import('./client-portal/client-portal.module').then((m) => m.ClientPortalModule),
    canActivate: [IsClientGuard],
    canLoad: [IsClientGuard],
  },
  {
    path: 'plans',
    loadChildren: () => import('./plans/plans.module').then((m) => m.PlansModule),
  },
  {
    path: 'terms',
    loadChildren: () => import('./terms/terms.module').then((m) => m.TermsModule),
  },
  {
    path: 'portal',
    loadChildren: () => import('./portal/portal.module').then((m) => m.PortalModule),
  },
  {
    path: 'invoice',
    loadChildren: () => import('./shared/public-invoice/public-invoice.module').then((m) => m.PublicInvoiceModule),
  },
  {
    path: '**',
    redirectTo: '',
    pathMatch: 'full',
  },
];

@NgModule({
  imports: [RouterModule.forRoot(routes)],
  exports: [RouterModule],
})
export class AppRoutingModule { }
